// Reusable CASH Q-Card: web copy of the customer app Q-Card (qpay2 q_card_page.dart),
// laid out at credit-card proportion (85.6 x 54 mm). The card is designed on a fixed
// 1011 x 638 px canvas (= 300 dpi) and scaled with CSS for on-screen display, so the
// exported PNG/PDF is pixel-identical to what is shown.
import React from 'react';
import QRCode from 'qrcode';
import logoRound from '../images/logoRound.png';

export const CARD_PX_W = 1011; // 85.6 mm @ 300 dpi
export const CARD_PX_H = 638; // 54 mm @ 300 dpi
export const CARD_MM_W = 85.6;
export const CARD_MM_H = 54;

const GREEN = '#5DA34A';
const LIGHT_GREEN = '#8CC152';
const DARK_GREEN = '#3E7D32';
const RED = '#E53935';
const FONT = "'Lato', 'Helvetica Neue', Arial, sans-serif";

// Black-on-white QR, error correction M (same as the app), with a quiet zone.
export const buildCardQrDataUrl = (qrData) =>
  QRCode.toDataURL(String(qrData || ''), {
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light: '#ffffff' },
    margin: 2,
    width: 600,
  });

const sideText = (rotate) => ({
  position: 'absolute',
  top: '50%',
  [rotate === 'left' ? 'left' : 'right']: 22,
  transform: `translate(${rotate === 'left' ? '-50%' : '50%'}, -50%) rotate(${rotate === 'left' ? -90 : 90}deg)`,
  whiteSpace: 'nowrap',
  color: 'rgba(255,255,255,0.9)',
  fontSize: 20,
  fontWeight: 800,
  letterSpacing: 3,
});

/**
 * Props:
 *  qrImage       data URL of the QR image (use buildCardQrDataUrl)
 *  customerName, customerNo
 *  width         displayed width in px (height follows the card ratio). Default = full 1011 px.
 *  rounded/shadow  on-screen styling; turn both off for exports (print-ready, full bleed)
 */
const CashQCard = React.forwardRef(function CashQCard(
  { qrImage, customerName, customerNo, width = CARD_PX_W, rounded = true, shadow = true },
  ref
) {
  const scale = width / CARD_PX_W;
  return (
    <div
      style={{
        width: CARD_PX_W * scale,
        height: CARD_PX_H * scale,
        overflow: 'hidden',
        borderRadius: rounded ? 24 * 1.6 * scale : 0,
        boxShadow: shadow ? '0 6px 16px rgba(0,0,0,0.18)' : 'none',
        background: '#fff',
      }}
    >
      <div
        ref={ref}
        style={{
          width: CARD_PX_W,
          height: CARD_PX_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          fontFamily: FONT,
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        {/* Green section */}
        <div
          style={{
            position: 'absolute', left: 0, top: 0, width: '100%', height: 500,
            background: `linear-gradient(to bottom, ${GREEN}, ${LIGHT_GREEN})`,
          }}
        >
          <div style={sideText('left')}>INSTANT REWARDS | SCAN TO EARN</div>
          <div style={sideText('right')}>INSTANT REWARDS | SCAN TO EARN</div>

          {/* Left column: logo pill, title, name, number */}
          <div style={{ position: 'absolute', left: 70, top: 44, width: 520 }}>
            <div
              style={{
                display: 'inline-flex', alignItems: 'center', background: '#fff', borderRadius: 60,
                padding: '10px 34px 10px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              }}
            >
              <img src={logoRound} alt="" style={{ width: 64, height: 64, objectFit: 'contain' }} />
              <span style={{ marginLeft: 12, fontSize: 48, fontWeight: 900, lineHeight: 1 }}>
                <span style={{ color: DARK_GREEN }}>Q</span>
                <span style={{ color: RED }}>-Pay</span>
              </span>
            </div>
            <div
              style={{
                marginTop: 34, color: '#fff', fontSize: 40, fontWeight: 900, fontStyle: 'italic',
                letterSpacing: 1.2, lineHeight: 1.1, textShadow: '0 2px 6px rgba(0,0,0,0.26)',
              }}
            >
              CUSTOMER CASHBACK CARD
            </div>
            <div
              style={{
                marginTop: 46, color: '#fff', fontSize: 38, fontWeight: 800, lineHeight: 1.15,
                maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                textShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }}
            >
              {customerName || 'Customer'}
            </div>
            <div style={{ marginTop: 10, color: '#fff', fontSize: 28, fontWeight: 700, letterSpacing: 2 }}>
              Q-Card ID: {customerNo || '--------'}
            </div>
          </div>

          {/* QR frame */}
          <div
            style={{
              position: 'absolute', right: 70, top: 50, width: 330, height: 400, boxSizing: 'border-box',
              border: '6px solid #fff', borderRadius: 36, padding: '12px 18px 18px', textAlign: 'center',
            }}
          >
            <div style={{ color: '#fff', fontSize: 30, fontWeight: 900, letterSpacing: 5, lineHeight: '40px' }}>
              SCAN ME
            </div>
            <div style={{ marginTop: 10, background: '#fff', borderRadius: 24, padding: 14 }}>
              {qrImage ? (
                <img src={qrImage} alt="Q-Card QR" style={{ display: 'block', width: 250, height: 250 }} />
              ) : (
                <div style={{ width: 250, height: 250 }} />
              )}
            </div>
          </div>
        </div>

        {/* Stripe band */}
        <div style={{ position: 'absolute', left: 0, top: 500, width: '100%' }}>
          <div style={{ height: 8, background: '#C5E1A5' }} />
          <div style={{ height: 6, background: '#fff' }} />
          <div style={{ height: 8, background: DARK_GREEN }} />
        </div>

        {/* Bottom grey section */}
        <div
          style={{
            position: 'absolute', left: 0, top: 522, width: '100%', height: 116, boxSizing: 'border-box',
            background: 'linear-gradient(to bottom, #F1F3F5, #D9DDE1)', padding: '16px 40px',
          }}
        >
          <div style={{ color: '#1F2937', fontSize: 28, fontWeight: 900, fontStyle: 'italic', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>
            MERCHANTS: SCAN TO CREDIT CASHBACK
          </div>
          <div style={{ position: 'absolute', right: 40, top: 14, textAlign: 'right' }}>
            <div style={{ color: '#6B7280', fontSize: 18, fontStyle: 'italic' }}>Powered by</div>
            <div style={{ fontSize: 30, lineHeight: 1.1 }}>
              <span style={{ color: '#1F2937', fontWeight: 700, fontStyle: 'italic' }}>Mythri </span>
              <span style={{ color: '#E8590C', fontWeight: 900 }}>Smart Bazaar</span>
            </div>
          </div>
          <div style={{ position: 'absolute', left: 40, bottom: 12, color: '#374151', fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>
            PRESENT AT CHECKOUT
          </div>
        </div>
      </div>
    </div>
  );
});

export default CashQCard;
